import React from 'react'
import { Link } from 'react-router-dom'
import { Avatar } from '@rallyshift/design-system'
import { BellOff, Megaphone, Users } from 'lucide-react'
import type { Conversation } from '../../api/client'
import { useLocale } from '../../contexts/LocaleContext'
import { listTime } from './format'

interface Props {
  conversations: Conversation[]
  activeId?: string
}

const ConversationList: React.FC<Props> = ({ conversations, activeId }) => {
  const { t, locale } = useLocale()

  const preview = (c: Conversation): string => {
    const m = c.last_message
    if (!m) return c.kind === 'direct' ? t('messages.kindDirect') : ''
    if (m.deleted) return t('messages.deleted')
    const text =
      m.body ||
      (m.attachment_kinds.includes('image') ? t('messages.photo') : m.attachment_kinds.length ? t('messages.document') : '')
    if (c.kind === 'direct' && !m.is_mine) return text
    const who = m.is_mine ? t('messages.you') : m.sender_first_name
    return who ? `${who}: ${text}` : text
  }

  return (
    <ul className="divide-y divide-border" aria-label={t('messages.title')}>
      {conversations.map((c) => {
        const active = c.id === activeId
        const unread = c.unread_count > 0 && !active
        return (
          <li key={c.id}>
            <Link
              to={`/messages/${c.id}`}
              aria-current={active ? 'page' : undefined}
              className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                active ? 'bg-tenant-soft' : 'hover:bg-surface-sunken'
              }`}
            >
              <ConversationIcon conversation={c} />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <p className={`truncate text-sm ${unread ? 'font-bold text-text-primary' : 'font-semibold text-text-primary'}`}>
                    {c.title}
                  </p>
                  {c.last_message_at && (
                    <span className="shrink-0 text-xs text-text-muted">{listTime(c.last_message_at, locale)}</span>
                  )}
                </div>
                {c.subtitle && <p className="truncate text-xs text-text-muted">{c.subtitle}</p>}
                <div className="mt-0.5 flex items-center gap-2">
                  <p className={`flex-1 truncate text-sm ${unread ? 'text-text-primary' : 'text-text-secondary'}`}>
                    {preview(c)}
                  </p>
                  {c.muted && <BellOff className="h-3.5 w-3.5 shrink-0 text-text-muted" aria-label={t('messages.muted')} />}
                  {unread && (
                    <span className="min-w-[20px] shrink-0 rounded-pill bg-tenant-brand px-1.5 text-center text-xs font-bold leading-5 text-tenant-on-brand">
                      {c.unread_count > 99 ? '99+' : c.unread_count}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          </li>
        )
      })}
    </ul>
  )
}

export const ConversationIcon: React.FC<{ conversation: Conversation; size?: number }> = ({
  conversation: c,
  size = 40,
}) => {
  if (c.kind === 'direct') {
    return <Avatar name={c.title} src={c.other_person?.avatar_url} size={size} />
  }
  const Icon = c.kind === 'organization' || c.kind === 'event' ? Megaphone : Users
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-full bg-tenant-soft text-tenant-ink"
      style={{ width: size, height: size }}
    >
      <Icon className="h-5 w-5" />
    </span>
  )
}

export default ConversationList
